import { useState } from 'react';
import { dtoVersSociete } from '../../api/mappers';
import { societeApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { SOC } from '../../constants/company';
import { OPTIONS_ST, REMISE_ANNUELLE, grilleDuSecteur, prixPeriode } from '../../constants/plans';
import { CHAMPS, SECTEURS } from '../../constants/trades';
import { TAUX_LEGAUX_ACTUELS } from '../../lib/tva';
import { C } from '../../theme/palette';

/**
 * Retire un nom laissé vide, pour que l'entreprise garde celui donné à
 * l'inscription. « Remplir plus tard » la rebaptisait « Votre entreprise »,
 * en tête de tous ses documents.
 *
 * @param {object} valeurs réglages remis par l'assistant
 * @returns {object} les mêmes, sans nom s'il est vide
 */
export function sansNomVide(valeurs) {
  if (valeurs.nom && String(valeurs.nom).trim()) return valeurs;
  const { nom: _ignore, ...reste } = valeurs;
  return reste;
}

/**
 * Coordonnées de la société du magasin, telles que le serveur les attend.
 *
 * @param {object} [magasin] coordonnées saisies dans l'onglet Magasin
 * @returns {object} les coordonnées, champs vides omis
 */
export function coordonneesDuMagasin(magasin) {
  const m = magasin || {};
  const champs = {
    nom: m.nom,
    adresse: m.adr,
    npa: m.npa,
    telephone: m.tel,
    email: m.email,
    iban: m.iban,
    numeroTva: m.tva,
    logo: m.logo,
  };
  return Object.fromEntries(
    Object.entries(champs).filter(([, v]) => typeof v === 'string' && v.trim() !== ''),
  );
}

/**
 * La fiche à garder après l'inscription : ce que l'assistant a saisi, et ce
 * que le serveur a enregistré du module, de la formule et du Pack IA.
 *
 * <p>Les paramètres viennent du serveur, qui y a posé l'état du Pack IA : les
 * reprendre est ce qui le rend visible partout où l'écran le lit, et ce qui
 * empêche le prochain enregistrement de la fiche — qui remplace les
 * paramètres en bloc — de l'effacer.
 *
 * @param {object|null} precedent fiche chargée avant l'assistant
 * @param {object} valeurs coordonnées et réglages saisis dans l'assistant
 * @param {object|null} fiche fiche rendue par le serveur après l'inscription
 * @returns {object} la fiche complète
 */
export function ficheApresInscription(precedent, valeurs, fiche) {
  const serveur = fiche ? dtoVersSociete(fiche) : {};
  return {
    ...precedent,
    ...valeurs,
    secteur: serveur.secteur || valeurs.secteur,
    abonnement: serveur.abonnement ?? precedent?.abonnement ?? null,
    parametres: {
      ...(precedent?.parametres || {}),
      ...(valeurs.parametres || {}),
      ...(serveur.parametres || {}),
    },
  };
}

/**
 * Assistant de démarrage : le module, la formule, puis les coordonnées.
 *
 * <p>Le module et la formule se choisissent ici, une fois : ils sont
 * enregistrés sur le compte par le serveur, et le module ne se change ensuite
 * que par la page Abonnement — il fixe la grille tarifaire.
 *
 * @param {object} props propriétés du composant
 * @param {(valeurs: object, fiche: object) => void} props.onDone appelé avec les
 *        coordonnées saisies et la fiche que le serveur a enregistrée
 * @param {object} [props.societe] la société née avec le compte : ses
 *        coordonnées, données à l'inscription, pré-remplissent le formulaire
 * @returns {import('react').ReactElement} l'étape en cours
 */
export function Onboarding({ onDone, societe }) {
  const [etape, setEtape] = useState(0);
  const [secteur, setSecteur] = useState(null);
  const [formule, setFormule] = useState(null);
  const [annuel, setAnnuel] = useState(false);
  const [packIa, setPackIa] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  // Le choix part au serveur avant que l'application ne s'ouvre : sans lui,
  // la societe n'aurait ni formule ni module enregistres. Le Mix, ce sont
  // deux societes : celle-ci devient le batiment, et le serveur cree la
  // societe du magasin avec les coordonnees saisies pour elle.
  const lance = async (valeurs, magasin) => {
    if (envoi) return;
    setErreur('');
    setEnvoi(true);
    try {
      const fiche = await societeApi.inscritAbonnement({
        secteur,
        formule,
        periodicite: annuel ? 'ANNUEL' : 'MENSUEL',
        packIa,
        ...(secteur === 'mixte' ? { societeMagasin: coordonneesDuMagasin(magasin) } : {}),
      });
      onDone(valeurs, fiche);
    } catch (e) {
      setErreur(e?.message || "Ton choix n'a pas pu être enregistré. Réessaie.");
      setEnvoi(false);
    }
  };
  // Ce que l'inscription a déjà demandé n'est pas redemandé : le nom,
  // l'adresse, le téléphone et l'e-mail reviennent tout remplis.
  const depart = {
    nom: societe?.nom || '',
    adr: societe?.adr || '',
    npa: societe?.npa || '',
    tel: societe?.tel || '',
    email: societe?.email || '',
    iban: societe?.iban || '',
    tva: societe?.tva || '',
  };
  const [form, setForm] = useState(depart);
  const [secEdit, setSecEdit] = useState('chantier');
  const [profils, setProfils] = useState({
    chantier: { ...depart, logo: societe?.logo || '' },
    magasin: { nom: '', adr: '', npa: '', tel: '', email: '', iban: '', tva: '', logo: '' },
  });
  const pf = profils[secEdit] || {};
  const setPf = (k, v) =>
    setProfils((p) => ({ ...p, [secEdit]: { ...(p[secEdit] || {}), [k]: v } }));
  if (etape === 0) {
    const BAT_ONLY = [
      'Devis : 5 modèles A4, signature client, conversion en facture',
      'Chantiers : heures, plans, avenants signés, réception, portail client, messagerie',
      'Équipe & paie suisse : AVS, AC, AANP, LPP, impôt source, 13ᵉ, fiches PDF',
      'Espace employé : pointage, rapports, photos, congés',
      'Planning des équipes, GPS & décharges A4',
    ];
    const MAG_ONLY = [
      'Offres : 5 modèles A4, signature client, conversion en facture',
      'Catalogue & stock en temps réel (seuils, code-barres, fournisseurs)',
      'Caisse & ventes : encaissement, ticket, rapport Z',
      'Remises personnalisées par client',
      'Commandes clients',
      'Bons de livraison signés → facture',
    ];
    const COMMON_F = [
      'Factures : QR-facture suisse, rappels, acomptes, récurrentes, remboursements',
      'Comptabilité, décompte TVA & échéancier',
      'Dépenses & frais (justificatifs, plan comptable suisse, export CSV)',
      'Clients & rendez-vous',
      'Outils & assistant IA (TVA, marge, métré, rédaction)',
      'Réglages multi-rôles & sauvegarde JSON',
    ];
    const MIX_X = [
      'Deux sociétés séparées sous un seul abonnement',
      'Chacune ses coordonnées, ses clients, ses documents et sa comptabilité',
      'Bascule Bâtiment ⇄ Magasin en un geste',
    ];
    const MODULES_ACCUEIL = [
      {
        ic: '🏗️',
        nom: 'Bâtiment / BTP',
        col: C.green,
        best: false,
        groups: [
          [null, BAT_ONLY],
          ['🧩 Gestion (incluse)', COMMON_F],
        ],
      },
      {
        ic: '🛒',
        nom: 'Magasin / Commerce',
        col: C.teal,
        best: false,
        groups: [
          [null, MAG_ONLY],
          ['🧩 Gestion (incluse)', COMMON_F],
        ],
      },
      {
        ic: '🔗',
        nom: 'Mix — bâtiment + magasin',
        col: C.amber,
        best: true,
        groups: [
          ['🏗️ Côté bâtiment', BAT_ONLY],
          ['🛒 Côté magasin', MAG_ONLY],
          ['🧩 Gestion (incluse)', COMMON_F],
          ['🔗 En plus avec le Mix', MIX_X],
        ],
      },
    ];
    return (
      <div
        style={{
          height: '100%',
          overflowY: 'auto',
          background: 'linear-gradient(160deg,' + C.navy + ',' + C.navyM + ' 55%, #0E1626)',
          padding: '40px 20px 34px',
        }}
      >
        <div style={{ maxWidth: 380, margin: '0 auto', textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: C.red,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 900,
              fontSize: 30,
              margin: '0 auto 16px',
            }}
          >
            S
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, color: '#fff', letterSpacing: -1 }}>
            SwissTech
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,.72)',
              marginTop: 8,
              maxWidth: 300,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.6,
            }}
          >
            Devis, chantiers, factures QR, paie suisse et comptabilité — tout au même endroit.
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'center',
              marginTop: 16,
            }}
          >
            {[
              `TVA ${TAUX_LEGAUX_ACTUELS.normal}% / ${TAUX_LEGAUX_ACTUELS.reduit}% / ${TAUX_LEGAUX_ACTUELS.hebergement}%`,
              'QR-facture suisse',
              'AVS / AI / AC / LPP',
              'Impôt à la source',
              'Plan comptable suisse',
              'Signature client en ligne',
              'Multi-rôles + code de connexion',
            ].map((t) => (
              <span
                key={t}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'rgba(255,255,255,.12)',
                  borderRadius: 99,
                  padding: '5px 11px',
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: 'rgba(255,255,255,.5)',
              textTransform: 'uppercase',
              letterSpacing: 1,
              margin: '26px 0 12px',
            }}
          >
            Une app, trois métiers
          </div>
          {MODULES_ACCUEIL.map((m) => (
            <div
              key={m.nom}
              style={{
                background: '#fff',
                borderRadius: 14,
                marginBottom: 12,
                textAlign: 'left',
                overflow: 'hidden',
                border: m.best ? '2px solid ' + m.col : 'none',
                boxShadow: m.best
                  ? '0 12px 30px rgba(224,138,30,.32)'
                  : '0 8px 22px rgba(0,0,0,.22)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  background: m.col + '18',
                  borderBottom: '1px solid ' + m.col + '33',
                  position: 'relative',
                }}
              >
                <span style={{ fontSize: 20 }}>{m.ic}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{m.nom}</span>
                {m.best && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 11,
                      right: 12,
                      background: m.col,
                      color: '#fff',
                      fontSize: 8,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      padding: '3px 8px',
                      borderRadius: 99,
                    }}
                  >
                    Le plus complet
                  </span>
                )}
              </div>
              <div style={{ padding: '8px 14px 12px' }}>
                {m.groups.map(([titre, feats], gi) => (
                  <div key={gi}>
                    {titre && (
                      <div
                        style={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          color: m.col,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          margin: (gi ? '10px' : '2px') + ' 0 3px',
                        }}
                      >
                        {titre}
                      </div>
                    )}
                    {feats.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'flex-start',
                          padding: '5px 0',
                          fontSize: 11,
                          color: C.ink,
                          lineHeight: 1.45,
                        }}
                      >
                        <span style={{ color: m.col, fontWeight: 900, marginTop: 1 }}>✓</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 8, width: '100%' }}>
            <Btn label="Commencer" color={C.red} full onClick={() => setEtape(1)} />
          </div>
          <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,.45)', marginTop: 12 }}>
            Tu choisiras ton métier à l'étape suivante — seules les fonctions utiles s'afficheront.
          </div>
        </div>
      </div>
    );
  }
  if (etape === 1)
    return (
      <div style={{ height: '100%', overflowY: 'auto', background: C.bg, padding: '40px 22px' }}>
        <div style={{ maxWidth: 380, margin: '0 auto' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.navy, marginBottom: 6 }}>
            Ton activité ?
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 22 }}>
            Pour adapter l'application à ton métier. Modifiable plus tard.
          </div>
          {SECTEURS.map(([id, ic, t, d]) => {
            const on = secteur === id;
            return (
              <div
                key={id}
                onClick={() => setSecteur(id)}
                style={{
                  background: C.white,
                  border: '2px solid ' + (on ? C.green : C.border),
                  borderRadius: 14,
                  padding: '14px 15px',
                  marginBottom: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 13,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: on ? C.greenL : C.navyXL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  {ic}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{t}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>{d}</div>
                </div>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: '2px solid ' + (on ? C.green : C.border),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    background: on ? C.green : 'transparent',
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  {on ? '✓' : ''}
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 8 }}>
            <Btn
              label="Continuer"
              color={C.navy}
              full
              disabled={!secteur}
              onClick={() => secteur && setEtape(2)}
            />
          </div>
        </div>
      </div>
    );
  if (etape === 2) {
    const plans = grilleDuSecteur(secteur);
    return (
      <div style={{ height: '100%', overflowY: 'auto', background: C.bg, padding: '40px 22px' }}>
        <div style={{ maxWidth: 380, margin: '0 auto' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.navy, marginBottom: 6 }}>
            Ta formule ?
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
            Toutes les fonctions sont incluses dans chaque formule : seuls le nombre d'accès
            complets (patrons, associés) et d'employés changent. 30 jours d'essai gratuit, sans
            carte bancaire.
          </div>
          <div
            style={{
              display: 'flex',
              background: C.navyXL,
              borderRadius: 11,
              padding: 4,
              marginBottom: 14,
            }}
          >
            {[
              [false, 'Mensuel'],
              [true, 'Annuel −' + Math.round(REMISE_ANNUELLE * 100) + '%'],
            ].map(([v, lb]) => (
              <div
                key={lb}
                role="button"
                onClick={() => setAnnuel(v)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: 9,
                  borderRadius: 8,
                  fontSize: 11.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: annuel === v ? '#fff' : 'transparent',
                  color: annuel === v ? C.ink : C.muted,
                }}
              >
                {lb}
              </div>
            ))}
          </div>
          {plans.map((p) => {
            const on = formule === p.id;
            return (
              <div
                key={p.id}
                role="button"
                aria-pressed={on}
                onClick={() => setFormule(p.id)}
                style={{
                  background: C.white,
                  border: '2px solid ' + (on ? p.col : C.border),
                  borderRadius: 14,
                  padding: '12px 14px',
                  marginBottom: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 20 }}>{p.ic}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{p.nom}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>
                    {p.users} accès complets · {p.emp} employés
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: p.col }}>
                    {prixPeriode(p, annuel)}.–
                  </div>
                  <div style={{ fontSize: 9.5, color: C.muted }}>
                    {annuel ? 'par an, HT' : 'par mois, HT'}
                  </div>
                </div>
              </div>
            );
          })}
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              background: C.white,
              border: '2px solid ' + (packIa ? C.purple : C.border),
              borderRadius: 14,
              padding: '12px 14px',
              marginTop: 4,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={packIa}
              onChange={(e) => setPackIa(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 800, color: C.ink }}>
                ✨ {OPTIONS_ST.packIA.nom}
              </span>
              <span style={{ display: 'block', fontSize: 9.5, color: C.muted, marginTop: 2 }}>
                +{OPTIONS_ST.packIA.prix}.–/mois · {OPTIONS_ST.packIA.limite}. Activable aussi plus
                tard, facturé à partir du mois d'activation.
              </span>
            </span>
          </label>
          <div style={{ marginTop: 14 }}>
            <Btn
              label="Continuer"
              color={C.navy}
              full
              disabled={!formule}
              onClick={() => formule && setEtape(3)}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <span
              onClick={() => setEtape(1)}
              style={{
                fontSize: 10,
                color: C.muted,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              ← Changer de module
            </span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ height: '100%', overflowY: 'auto', background: C.bg, padding: '40px 22px' }}>
      <div style={{ maxWidth: 380, margin: '0 auto' }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: C.navy, marginBottom: 6 }}>
          {secteur === 'mixte' ? 'Tes deux sociétés' : 'Ta société'}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 20 }}>
          {secteur === 'mixte'
            ? 'En mode Mix, le Bâtiment et le Magasin sont deux sociétés séparées : renseigne les coordonnées de chacune. Elles apparaîtront sur leurs propres documents.'
            : 'Ces informations figureront sur tes devis et factures.'}
        </div>

        {secteur === 'mixte' && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[
              ['chantier', '🏗 Bâtiment'],
              ['magasin', '🛒 Magasin'],
            ].map(([s, lb]) => {
              const on = secEdit === s;
              const rempli = (profils[s]?.nom || '').trim();
              return (
                <div
                  key={s}
                  onClick={() => setSecEdit(s)}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '10px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer',
                    background: on ? C.navy : C.white,
                    color: on ? '#fff' : C.grey,
                    border: '1px solid ' + (on ? C.navy : C.border),
                  }}
                >
                  {lb}
                  {rempli ? ' ✓' : ''}
                </div>
              );
            })}
          </div>
        )}

        <Card>
          <div style={{ padding: 14 }}>
            {secteur === 'mixte' ? (
              <>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: pf.logo ? '#fff' : C.navyXL,
                      border: '1px solid ' + C.border,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {pf.logo ? (
                      <img
                        src={pf.logo}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        alt=""
                      />
                    ) : (
                      <span style={{ fontSize: 11, color: C.muted }}>logo</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: 'inline-block',
                        background: C.navy,
                        color: '#fff',
                        borderRadius: 9,
                        padding: '7px 11px',
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      📷 Logo {secEdit === 'magasin' ? 'magasin' : 'bâtiment'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) {
                            const r = new FileReader();
                            r.onload = () => setPf('logo', r.result);
                            r.readAsDataURL(f);
                          }
                        }}
                      />
                    </label>
                    <input
                      value={(pf.logo || '').startsWith('http') ? pf.logo : ''}
                      onChange={(e) => setPf('logo', e.target.value)}
                      placeholder="…ou coller un lien (URL)"
                      style={{
                        width: '100%',
                        border: '1px solid ' + C.border,
                        borderRadius: 8,
                        padding: '7px 9px',
                        fontSize: 9.5,
                        marginTop: 6,
                      }}
                    />
                  </div>
                </div>
                {CHAMPS.map(([lb, key, ph]) => (
                  <Inp
                    key={key}
                    label={lb}
                    placeholder={ph}
                    value={pf[key] || ''}
                    onChange={(e) => setPf(key, e.target.value)}
                  />
                ))}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: form.logo ? '#fff' : C.navyXL,
                      border: '1px solid ' + C.border,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {form.logo ? (
                      <img
                        src={form.logo}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        alt=""
                      />
                    ) : (
                      <span style={{ fontSize: 11, color: C.muted }}>logo</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: 'inline-block',
                        background: C.navy,
                        color: '#fff',
                        borderRadius: 9,
                        padding: '7px 11px',
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      📷 Importer un logo
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) {
                            const r = new FileReader();
                            r.onload = () => setForm((p) => ({ ...p, logo: r.result }));
                            r.readAsDataURL(f);
                          }
                        }}
                      />
                    </label>
                    <input
                      value={(form.logo || '').startsWith('http') ? form.logo : ''}
                      onChange={(e) => setForm((p) => ({ ...p, logo: e.target.value }))}
                      placeholder="…ou coller un lien (URL)"
                      style={{
                        width: '100%',
                        border: '1px solid ' + C.border,
                        borderRadius: 8,
                        padding: '7px 9px',
                        fontSize: 9.5,
                        marginTop: 6,
                      }}
                    />
                  </div>
                </div>
                {CHAMPS.map(([lb, key, ph]) => (
                  <Inp
                    key={key}
                    label={lb}
                    placeholder={ph}
                    value={form[key]}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  />
                ))}
              </>
            )}
          </div>
        </Card>

        {secteur === 'mixte' && (
          <div style={{ fontSize: 9, color: C.muted, textAlign: 'center', marginTop: 8 }}>
            {(profils.chantier?.nom || '').trim() && (profils.magasin?.nom || '').trim()
              ? '✓ Les deux secteurs sont renseignés'
              : 'Renseigne les deux secteurs (ou complète plus tard dans Réglages)'}
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            background: C.navy,
            color: '#fff',
            borderRadius: 12,
            padding: '13px 15px',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800 }}>🔒 Sécurité de ton compte</div>
          <div
            style={{ fontSize: 10, color: 'rgba(255,255,255,.82)', marginTop: 6, lineHeight: 1.6 }}
          >
            Dès la connexion, active la <b>double authentification</b> (code par SMS ou
            Authenticator) et <b>garde ton code secret</b> — ne le communique jamais à personne.
            Tous les détails à tout moment dans <b>Plus → 🔒 Sécurité</b>.
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          {erreur && (
            <div
              role="alert"
              style={{ fontSize: 11, color: C.red, marginBottom: 8, textAlign: 'center' }}
            >
              {erreur}
            </div>
          )}
          <Btn
            label={envoi ? 'Enregistrement…' : '✓ Lancer SwissTech'}
            color={C.green}
            full
            disabled={envoi}
            onClick={() => {
              if (secteur === 'mixte') {
                // Deux sociétés : les coordonnées du bâtiment vont à celle-ci,
                // celles du magasin à la société que le serveur crée à côté.
                const batiment = profils.chantier || {};
                lance(
                  sansNomVide({
                    ...SOC,
                    ...Object.fromEntries(Object.entries(batiment).filter(([, v]) => v !== '')),
                    nom: batiment.nom,
                    secteur,
                    devise: 'CHF',
                  }),
                  profils.magasin,
                );
              } else {
                lance(sansNomVide({ ...SOC, ...form, nom: form.nom, secteur, devise: 'CHF' }));
              }
            }}
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <span
            onClick={() =>
              lance(
                sansNomVide({
                  ...SOC,
                  nom: '',
                  secteur,
                  devise: 'CHF',
                }),
                profils.magasin,
              )
            }
            style={{ fontSize: 10, color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
          >
            Remplir plus tard
          </span>
        </div>
      </div>
    </div>
  );
}
